import { handleResponseOld, handleResponseWithNon200Errors, makeGet, makePost } from './helpers';
import { BiddingInfo } from '@/types/BiddingInfo';
import { CatalogRegistration } from '@/types/CatalogRegistration';

/**
 * @category Item API
 * @see fetchBidderData
 */
type FetchBidderDataParams = {
    authToken: string;
    deployment: string;
};

type FetchBidderDataResponse = {
    data: {
        annualNMS: number;
        bidder: {
            acceptedTermsOfService: string;
            address: string;
            address2: string;
            bidderId: number;
            city: string;
            country: string;
            createdDate: string;
            creditCardValidated: boolean;
            email: string;
            firstName: string;
            hasEverRegisteredForAuction: boolean;
            lastName: string;
            mobileNumber: string;
            phoneNumber: string;
            profileComplete: boolean;
            standing: string;
            state: string;
            username: string;
            zip: string;
            zipCodeGeoCoordinates: {
                latitude: number;
                longitude: number;
            };
        };
        biddingInfos: BiddingInfo[];
        catalogRegistration: CatalogRegistration[];
        followedSellers: number[];
        resellerType: number;
        savedItems: number[];
        savedItemsTime: [
            {
                itemId: string;
                saveTs: number;
            }
        ];
        searchExclusions: {
            houseIds: number[];
        };
        stripeCustomer: any;
    };
    message: string;
    success: boolean;
};

/**
 * GET request to item-api to load Bidder data
 * @function fetchBidderData
 * @category Item API
 * @param {FetchBidderDataParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/spauser
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/get-spa-user.ts
 */
export const fetchBidderData = ({ authToken, deployment }: FetchBidderDataParams) =>
    new Promise<FetchBidderDataResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>auth/spauser' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postBidderConsent
 */
type PostBidderConsentParams = {
    authToken: string;
    consent: any;
    context: any;
    deployment: string;
};

/**
 * POST request to user api that
 * @function postBidderConsent
 * @category User API
 * @param {PostBidderConsentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/accepttermsofservice
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/yzjvui/methods/POST
 * @see https://github.com/LIVEauctioneers/Flynn/tree/master/functions/FlynnAcceptTermsOfServiceV2
 */
export const postBidderConsent = ({ authToken, consent, context, deployment }: PostBidderConsentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'accepttermsofservice', authToken, deployment, path: '<USER-API>' });
        request.send({ acceptedTermsOfService: consent, context, signUpClient: 'web' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchUpcomingItemData
 */
type FetchUpcomingDataParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

/**
 * @category Item API
 * @see fetchUpcomingItemData
 */
type FetchUpcomingDataResponse = {
    data: number[];
    message: string;
    success: boolean;
};

/**
 * GET request to item-api for bidder saved items that are upcoming for auction
 * @function fetchUpcomingItemData
 * @category Item API
 * @param {FetchUpcomingDataParams}
 * @returns {FetchUpcomingDataResponse}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/upcoming-items/bidder
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/get-bidder-upcoming-items.ts
 */
export const fetchUpcomingItemData = ({ authToken, bidderId, deployment }: FetchUpcomingDataParams) =>
    new Promise<FetchUpcomingDataResponse>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: '<ITEM-API>upcoming-items/bidder',
            queryParams: { bidderId },
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export type UpdateProfileRequest = {
    address1: string;
    address2: string;
    city: string;
    country: string;
    email: string;
    firstName: string;
    lastName: string;
    mobile: string;
    phoneNumber?: string;
    state: string;
    zipcode: string;
};
/**
 * @category User API
 * @see postUpdateProfile
 */
type PostUpdateProfileParams = {
    authToken: string;
    deployment: string;
    profile: UpdateProfileRequest;
};

/**
 * POST to Flynn user API service to update Bidder profile data
 * @function postUpdateProfile
 * @category User API
 * @param {PostUpdateProfileParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/updateprofile
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/5zbu5t/methods/POST
 * @see https://github.com/LIVEauctioneers/Flynn/tree/master/functions/FlynnUpdateBidderProfileV2
 */
export const postUpdateProfile = ({ authToken, deployment, profile }: PostUpdateProfileParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'updateprofile', authToken, deployment, path: '<USER-API>' });

        // TODO: Flynn wants some fields in a different format, we need to get this standardized accross all apps
        const body: any = {
            address: profile.address1,
            address2: profile.address2,
            city: profile.city,
            country: profile.country,
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            mobileNumber: profile.mobile,
            // phoneNumber: '',
            state: profile.state,
            zip: profile.zipcode,
        };

        request.send(body);
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postCreateAccount
 */
type PostCreateAccountParams = {
    agree: any;
    deployment: string;
    emailAddress: string;
    signupReferralHouse: number;
};

/**
 * POST to Flynn user API service to create a new user account
 * @function postCreateAccount
 * @category User API
 * @param {PostCreateAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/createuser
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/nci812/methods/POST
 * @see https://github.com/LIVEauctioneers/Flynn/tree/master/functions/FlynnCreateBidderV2
 */
export const postCreateAccount = ({ agree, deployment, emailAddress, signupReferralHouse }: PostCreateAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'createuser', deployment, path: '<USER-API>' });
        const requestBody: any = {
            acceptedTermsOfService: agree,
            client_version: '4.3',
            email_address: emailAddress,
            signup_client: 'web',
        };

        if (signupReferralHouse) {
            requestBody.referralHouse = `${signupReferralHouse}`;
        }
        request.send(requestBody);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postDeleteAccount
 */
type PostDeleteAccountParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

/**
 * POST to Flynn user API service to delete a user account
 * @function postDeleteAccount
 * @category User API
 * @param {PostDeleteAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/remove/${bidderId}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/ncmbuj/methods/POST
 * @see https://github.com/LIVEauctioneers/Flynn/tree/master/functions/FlynnRemoveBidder
 */
export const postDeleteAccount = ({ authToken, bidderId, deployment }: PostDeleteAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: `<USER-API>remove/${bidderId}` });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postLogin
 */
type PostLoginParams = {
    deployment: string;
    mainhostLogin: any;
    password: string | null | undefined;
    username: string;
};

/**
 * POST to item-api service to authenticate and log in a user
 * @function postLogin
 * @category Item API
 * @param {PostLoginParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/auth/spalogin
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-login.ts
 */
export const postLogin = ({ deployment, mainhostLogin, password, username }: PostLoginParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>auth/spalogin' });
        request.type('form');
        request.send({ password, username, ...mainhostLogin });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category User API
 * @see postSetPassword
 */
type PostSetPasswordParams = {
    authToken: string;
    deployment: string;
    password: string;
};

/**
 * POST to Flynn User API service to create a password for a user account
 * @function postSetPassword
 * @category User API
 * @param {PostSetPasswordParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/user/createpassword
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/g3qvanjl6a/resources/63czot/methods/POST
 * @see https://github.com/LIVEauctioneers/Flynn/tree/master/functions/FlynnCreatePasswordV2
 */
export const postSetPassword = ({ authToken, deployment, password }: PostSetPasswordParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'createpassword', authToken, deployment, path: '<USER-API>' });
        request.send({ password });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postSetSearchFacetOptionExclusion
 */
type PostSetSearchFacetOptionExclusionParams = {
    authToken: string;
    deployment: string;
    exclude: any;
    facetId: any;
    optionId: any;
};

/**
 * POST to item-api to save Search Facet exclusions for user account
 * @function postSetSearchFacetOptionExclusion
 * @category Item API
 * @param {PostSetSearchFacetOptionExclusionParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/search/exclusion/seller/${optionId}/${excludeBit}
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/post-search-exclusion.ts
 */
export const postSetSearchFacetOptionExclusion = ({
    authToken,
    deployment,
    exclude,
    facetId,
    optionId,
}: PostSetSearchFacetOptionExclusionParams) =>
    new Promise<any>((resolve, reject) => {
        let requestURL;
        // transform the `exclude` bool into a number (0 or 1)
        const excludeBit = ~~exclude;

        switch (facetId) {
            case 'auctionHouse':
                requestURL = `<ITEM-API>search/exclusion/seller/${optionId}/${excludeBit}`;
                break;
            default:
                return reject(`Excluding search results by ${facetId} is not yet supported.`);
        }
        const request = makePost({ authToken, deployment, path: requestURL });
        request.send();
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
